/* eslint-disable indent */
import { Component } from '@verndale/core';
import { isFocusable, open, close } from '../../helpers';

class Module extends Component {
  setupDefaults() {
    this.dom = {
      body: document.querySelector('body'),
      close: this.el.querySelector('.cookie-banner__details-close'),
      toggles: [...this.el.querySelectorAll('.cookie-banner__details-toggle')],
      tables: [
        ...this.el.querySelectorAll('.cookie-banner__details-table table')
      ]
    };

    this.isOpen = false;
    this.ignoreUtilFocusChanges = false;
    this.lastFocus = null;
    this.scrollTop = document.documentElement.scrollTop;

    this.dom.tables.forEach(table => {
      table.parentNode.innerHTML = this.buildTable(table);
    });
  }

  addListeners() {
    document.addEventListener('scroll', this.handleScroll.bind(this));
    document.addEventListener('keyup', this.handleEscape.bind(this));
    document.addEventListener('focus', this.handleFocus.bind(this), true);
    this.dom.close.addEventListener('click', this.handleOnClose.bind(this));

    this.dom.toggles.forEach(toggle => {
      toggle.addEventListener('click', this.handleToggleDetails.bind(this));
    });

    [
      ...this.el.querySelectorAll('.cookie-banner__details-item-trigger')
    ].forEach(toggle => {
      toggle.addEventListener('click', this.handleToggleCookie.bind(this));
    });
  }

  open() {
    this.dom.body.style.top = `-${this.scrollTop}px`;
    this.dom.body.classList.add('has-modal');
    this.isOpen = true;
    this.el.style.display = 'flex';
    this.dom.close.focus();

    setTimeout(() => {
      this.el.classList.add('modal--active');
    }, 0);
  }

  close() {
    this.closeModal();
  }

  handleToggleDetails(e) {
    const toggle = e.currentTarget;
    const table = toggle.nextElementSibling;
    const label = toggle.querySelector('span');

    if (toggle.dataset.isOpen === 'true') {
      label.innerHTML = toggle.dataset.showMore;
      toggle.dataset.isOpen = false;
      close({ element: table });
    } else {
      label.innerHTML = toggle.dataset.showLess;
      toggle.dataset.isOpen = true;
      open({
        element: table,
        onComplete: () => {
          table.style.height = 'auto';
        }
      });
    }
  }

  handleToggleCookie(e) {
    const toggle = e.currentTarget;
    const table = toggle.nextElementSibling;

    if (toggle.dataset.isOpen === 'true') {
      toggle.dataset.isOpen = false;
      close({ element: table });
    } else {
      toggle.dataset.isOpen = true;
      open({
        element: table
      });
    }
  }

  handleScroll() {
    if (this.isOpen) return;

    this.scrollTop = document.documentElement.scrollTop;
  }

  handleEscape(e) {
    if (!this.isOpen) return;

    const key = e.which || e.keyCode;

    if (key === 27 && this.closeModal()) {
      e.stopPropagation();
    }
  }

  handleFocus(e) {
    if (!this.isOpen || this.ignoreUtilFocusChanges) return;

    if (this.el.contains(e.target)) {
      this.lastFocus = e.target;
    } else {
      this.focusFirstDescendant(this.el);

      if (this.lastFocus === document.activeElement) {
        this.focusLastDescendant(this.el);
      }
      this.lastFocus = document.activeElement;
    }
  }

  handleOnClose(e) {
    e.preventDefault();
    this.closeModal();
  }

  closeModal() {
    const top = this.scrollTop;
    this.dom.body.style.top = null;
    this.dom.body.classList.remove('has-modal');
    this.isOpen = false;
    this.el.classList.remove('modal--active');
    this.el.focus();
    window.scrollTo(0, top);

    setTimeout(() => {
      this.el.style.display = 'none';
    }, 250);
  }

  focusFirstDescendant(element) {
    for (let i = 0; i < element.childNodes.length; i++) {
      const child = element.childNodes[i];
      if (this.attemptFocus(child) || this.focusFirstDescendant(child)) {
        return true;
      }
    }
    return false;
  }

  focusLastDescendant(element) {
    for (let i = element.childNodes.length - 1; i >= 0; i--) {
      const child = element.childNodes[i];
      if (this.attemptFocus(child) || this.focusLastDescendant(child)) {
        return true;
      }
    }
    return false;
  }

  attemptFocus(element) {
    if (!isFocusable(element)) {
      return false;
    }

    this.ignoreUtilFocusChanges = true;
    try {
      element.focus();
    } catch (e) {
      return;
    }
    this.ignoreUtilFocusChanges = false;
    return document.activeElement === element;
  }

  buildTable(table) {
    const headdings = [...table.querySelectorAll('thead th')].map(
      h => h.innerHTML
    );

    if (!headdings.length) {
      return '<div class="cookie-banner__details-item cookie-banner__details-item--empty">We do not use cookies of this type.</div>';
    }

    const providers = [];

    [...table.querySelectorAll('tbody tr')].forEach(tr => {
      let provider;
      const cookie = {};
      [...tr.querySelectorAll('td')].forEach((td, i) => {
        if (i === 1) {
          const a = td.querySelector('a');
          provider = providers.find(
            provider => provider.name === (a ? a.text : td.innerHTML)
          );

          if (!provider) {
            provider = {
              name: a ? a.text : td.innerHTML,
              cookies: []
            };
            providers.push(provider);
          }
        } else {
          cookie[headdings[i]] = td.innerHTML;
        }
      });
      provider.cookies.push(cookie);
    });

    return this.buildTableMarkup(
      providers.sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      })
    );
  }

  buildTableMarkup(providers) {
    return providers
      .map(
        provider =>
          `
          <div class="cookie-banner__details-item">
            <button class="cookie-banner__details-item-trigger">
              <i role="presentation"></i>${provider.name} (${
            provider.cookies.length
          })
            </button>
            <div class="cookie-banner__details-item-cookies">
              <div class="cookie-banner__details-item-cookies-inner">
                ${provider.cookies
                  .map(
                    cookie => `
                    <div class="cookie-banner__details-item-cookie">
                      <div class="cookie-banner__details-item-row">
                        <p class="cookie-banner__details-item-label">${cookie.Name}</p>
                        <p>${cookie.Purpose}</p>
                      </div>
                      <div class="cookie-banner__details-item-dual-row">
                        <div>
                          <p class="cookie-banner__details-item-label">Expiry</p>
                          <p>${cookie.Expiry}</p>
                        </div>
                        <div>
                          <p class="cookie-banner__details-item-label">Type</p>
                          <p>${cookie.Type}</p>
                        </div>
                      </div>
                    </div>
                `
                  )
                  .join('')}
              </div>
            </div>
          </div>
      `
      )
      .join('');
  }
}

export default Module;
